import { useLazyQuery } from '@apollo/client';
import { useEffect, useState } from 'react';

import {
  GetTakenJobDetailsQueryDocument,
  type DreamJobItemFragment,
  type TakenJobFragment,
} from '@xing-com/crate-dream-machine-graphql';
import { serialize } from '@xing-com/crate-dream-machine-hooks';
import type { JobCardData } from '@xing-com/crate-dream-machine-types';

import {
  GetDreamMachineModalSearchAlertsDocument,
  type GetDreamMachineModalSearchAlertsQuery,
} from './get-dream-machine-modal-search-alerts.gql-types';

type UseModalData = (
  cardId: string,
  show: boolean
) => {
  jobCardData: JobCardData | undefined;
  alertId: string | undefined;
  loading: boolean;
  error: Error | undefined;
};

const getAlertForCard = (
  takenJob: TakenJobFragment,
  alertData: GetDreamMachineModalSearchAlertsQuery
): string | undefined => {
  const searchAlerts =
    alertData?.viewer?.searchAlerts?.__typename === 'SearchAlertResultResponse'
      ? alertData?.viewer?.searchAlerts?.collection?.filter(
          (alert) => alert !== null
        ) || []
      : [];

  const company = takenJob.job?.company || takenJob.job?.fallbackCompany;
  const location = company?.address?.city || '';
  const keywords = [takenJob.job?.jobTitle, takenJob.job?.company?.companyName]
    .filter((item) => !!item)
    .join(' ');

  return searchAlerts.find((searchAlert) => {
    if (
      searchAlert.query?.body?.keywords === keywords &&
      searchAlert.query?.body?.location?.text === location
    ) {
      return searchAlert.id;
    }
    return undefined;
  })?.id;
};

export const useModalData: UseModalData = (cardId, show) => {
  const [modalData, setModalData] = useState<{
    jobCard: JobCardData;
    alertId: string | undefined;
  }>();
  const [fetchJobDetails, { data, loading }] = useLazyQuery(
    GetTakenJobDetailsQueryDocument,
    {
      errorPolicy: 'all',
      fetchPolicy: 'no-cache',
      notifyOnNetworkStatusChange: true,
      variables: { id: cardId },
    }
  );
  const [getAlertData, { data: alertData, loading: alertLoading }] =
    useLazyQuery(GetDreamMachineModalSearchAlertsDocument, {
      errorPolicy: 'all',
      fetchPolicy: 'no-cache',
      notifyOnNetworkStatusChange: true,
    });

  useEffect(() => {
    if (!loading && !alertLoading && data && alertData) {
      const takenJob: DreamJobItemFragment = {
        __typename: 'TakenJob',
        id: '',
        createdAt: null,
        takenJobUrn: '',
        takenJobTrackingToken: '',
        job: data?.dreamJobsFetchTakenJobDetails?.[0],
      };

      const searchAlertId = getAlertForCard(takenJob, alertData);

      setModalData({
        jobCard: serialize([takenJob], [])[0],
        alertId: searchAlertId,
      });
    }
  }, [loading, alertLoading, alertData, data]);

  useEffect(() => {
    if (show && cardId) {
      fetchJobDetails();
      getAlertData();
    }
  }, [show, cardId, fetchJobDetails, getAlertData]);

  return {
    jobCardData: modalData?.jobCard,
    alertId: modalData?.alertId,
    loading: false,
    error: undefined,
  };
};
